import React, { useState } from "react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import { Calendar, FileText, Calculator, Clock, CheckCircle, AlertCircle } from "lucide-react";
import { companyInfo, getServices } from "../mock";
import { useTranslation } from "../hooks/useTranslation";
import { useLanguage } from "../contexts/LanguageContext";

const Quote = () => {
  const { t } = useTranslation();
  const { language } = useLanguage();
  
  const services = getServices(language);
  
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    // Step 1: Personal Info
    name: "",
    email: "",
    phone: "",
    address: "",
    
    // Step 2: Project Details
    projectType: "",
    services: [],
    budget: "",
    timeline: "",
    description: "",
    
    // Step 3: Property Details
    propertyType: "",
    propertySize: "",
    currentCondition: "",
    accessRequirements: "",
    
    // Step 4: Preferences
    preferredContact: "",
    visitDate: "",
    additionalInfo: ""
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleServiceToggle = (serviceId) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.includes(serviceId)
        ? prev.services.filter(id => id !== serviceId)
        : [...prev.services, serviceId]
    }));
  };

  const nextStep = () => {
    if (currentStep < 5) setCurrentStep(currentStep + 1);
  };

  const prevStep = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  const handleSubmit = () => {
    // Préparer le message WhatsApp avec les détails du devis
    const selectedServices = formData.services.map(id => {
      const service = services.find(s => s.id === id);
      return service ? service.title : '';
    }).filter(title => title).join(', ');
    
    const whatsappMessage = `Bonjour Mohamed,
    
Je souhaite faire une demande de devis pour AYMORENOV CONSTRUCTION INC:

👤 INFORMATIONS CLIENT:
• Nom: ${formData.name}
• Email: ${formData.email}
• Téléphone: ${formData.phone}
• Adresse du projet: ${formData.address}

🏠 DÉTAILS DU PROJET:
• Services demandés: ${selectedServices || 'À préciser'}
• Budget approximatif: ${formData.budget || 'À discuter'}
• Délai souhaité: ${formData.timeline || 'Flexible'}
• Type de propriété: ${formData.propertyType || 'À préciser'}

📝 DESCRIPTION:
${formData.description || 'À discuter lors de la consultation'}

Merci de me contacter pour planifier une évaluation gratuite!`;

    const whatsappUrl = `https://wa.me/5065884919?text=${encodeURIComponent(whatsappMessage)}`;
    window.open(whatsappUrl, '_blank');
    
    alert("Votre demande va être envoyée via WhatsApp à Mohamed Obad. La conversation s'ouvrira dans un nouvel onglet.");
  };

  const StepIndicator = () => (
    <div className="flex items-center justify-center mb-8">
      {[1, 2, 3, 4, 5].map((step) => (
        <div key={step} className="flex items-center">
          <div 
            className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold ${
              step <= currentStep ? 'opacity-100' : 'opacity-50'
            }`}
            style={{ backgroundColor: step <= currentStep ? "#A04030" : "#D1D5DB" }}
          >
            {step < currentStep ? <CheckCircle className="w-5 h-5" /> : step}
          </div>
          {step < 5 && (
            <div 
              className={`w-16 h-1 mx-2 ${step < currentStep ? 'opacity-100' : 'opacity-30'}`}
              style={{ backgroundColor: step < currentStep ? "#D4A86A" : "#D1D5DB" }}
            />
          )}
        </div>
      ))}
    </div>
  );

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <Card className="shadow-xl">
            <CardHeader>
              <CardTitle className="flex items-center text-2xl" style={{ color: "#283747" }}>
                <FileText className="w-6 h-6 mr-3" />
                {t('quotePage.step1')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: "#283747" }}>
                    Nom complet *
                  </label>
                  <Input
                    value={formData.name}
                    onChange={(e) => handleInputChange("name", e.target.value)}
                    className="border-2 border-slate-200 focus:border-yellow-500"
                    placeholder="Votre nom complet"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: "#283747" }}>
                    Téléphone *
                  </label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => handleInputChange("phone", e.target.value)}
                    className="border-2 border-slate-200 focus:border-yellow-500"
                    placeholder="+1 (514) 123-4567"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: "#283747" }}>
                  Email *
                </label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                  className="border-2 border-slate-200 focus:border-yellow-500"
                  placeholder="votre@email.com"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: "#283747" }}>
                  Adresse du projet *
                </label>
                <Input
                  value={formData.address}
                  onChange={(e) => handleInputChange("address", e.target.value)}
                  className="border-2 border-slate-200 focus:border-yellow-500"
                  placeholder="Adresse complète où auront lieu les travaux"
                />
              </div>
            </CardContent>
          </Card>
        );
        
      case 2:
        return (
          <Card className="shadow-xl">
            <CardHeader>
              <CardTitle className="flex items-center text-2xl" style={{ color: "#283747" }}>
                <Calculator className="w-6 h-6 mr-3" />
                Détails du Projet
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <label className="block text-sm font-medium mb-4" style={{ color: "#283747" }}>
                  Services demandés * (Sélectionnez tous ceux qui s'appliquent)
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {services.map((service) => (
                    <div key={service.id} className="flex items-start space-x-3 p-4 border-2 border-slate-200 rounded-lg hover:border-yellow-500 transition-colors">
                      <Checkbox
                        checked={formData.services.includes(service.id)}
                        onCheckedChange={() => handleServiceToggle(service.id)}
                        style={{ accentColor: "#A04030" }}
                      />
                      <div className="flex-1">
                        <h4 className="font-medium" style={{ color: "#283747" }}>
                          {service.title}
                        </h4>
                        <p className="text-sm text-slate-600 mt-1">{service.description}</p>
                        <Badge 
                          className="mt-2 text-white"
                          style={{ backgroundColor: "#D4A86A" }}
                        >
                          Devis sur mesure
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: "#283747" }}>
                    Budget approximatif
                  </label>
                  <Select value={formData.budget} onValueChange={(value) => handleInputChange("budget", value)}>
                    <SelectTrigger className="border-2 border-slate-200 focus:border-yellow-500">
                      <SelectValue placeholder="Sélectionner votre budget" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="5000-15000">5 000$ - 15 000$</SelectItem>
                      <SelectItem value="15000-30000">15 000$ - 30 000$</SelectItem>
                      <SelectItem value="30000-50000">30 000$ - 50 000$</SelectItem>
                      <SelectItem value="50000-100000">50 000$ - 100 000$</SelectItem>
                      <SelectItem value="100000+">Plus de 100 000$</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: "#283747" }}>
                    Délai souhaité
                  </label>
                  <Select value={formData.timeline} onValueChange={(value) => handleInputChange("timeline", value)}>
                    <SelectTrigger className="border-2 border-slate-200 focus:border-yellow-500">
                      <SelectValue placeholder="Quand souhaitez-vous commencer ?" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="immediate">Immédiatement</SelectItem>
                      <SelectItem value="1-month">Dans le mois</SelectItem>
                      <SelectItem value="2-3-months">2-3 mois</SelectItem>
                      <SelectItem value="6-months">Dans 6 mois</SelectItem>
                      <SelectItem value="flexible">Flexible</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: "#283747" }}>
                  Description détaillée du projet
                </label>
                <Textarea
                  rows={4}
                  value={formData.description}
                  onChange={(e) => handleInputChange("description", e.target.value)}
                  className="border-2 border-slate-200 focus:border-yellow-500 resize-none"
                  placeholder="Décrivez votre vision, vos besoins spécifiques, les défis particuliers..."
                />
              </div>
            </CardContent>
          </Card>
        );
        
      case 3:
        return (
          <Card className="shadow-xl">
            <CardHeader>
              <CardTitle className="flex items-center text-2xl" style={{ color: "#283747" }}>
                <FileText className="w-6 h-6 mr-3" />
                Informations sur la Propriété
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: "#283747" }}>
                    Type de propriété
                  </label>
                  <Select value={formData.propertyType} onValueChange={(value) => handleInputChange("propertyType", value)}>
                    <SelectTrigger className="border-2 border-slate-200 focus:border-yellow-500">
                      <SelectValue placeholder="Type de propriété" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="house">Maison unifamiliale</SelectItem>
                      <SelectItem value="condo">Condo/Appartement</SelectItem>
                      <SelectItem value="duplex">Duplex/Triplex</SelectItem>
                      <SelectItem value="commercial">Commercial</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: "#283747" }}>
                    Superficie approximative
                  </label>
                  <Select value={formData.propertySize} onValueChange={(value) => handleInputChange("propertySize", value)}>
                    <SelectTrigger className="border-2 border-slate-200 focus:border-yellow-500">
                      <SelectValue placeholder="Superficie" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="small">Moins de 1000 pi²</SelectItem>
                      <SelectItem value="medium">1000 - 2000 pi²</SelectItem>
                      <SelectItem value="large">2000 - 3000 pi²</SelectItem>
                      <SelectItem value="xlarge">Plus de 3000 pi²</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: "#283747" }}>
                  État actuel de la propriété
                </label>
                <Select value={formData.currentCondition} onValueChange={(value) => handleInputChange("currentCondition", value)}>
                  <SelectTrigger className="border-2 border-slate-200 focus:border-yellow-500">
                    <SelectValue placeholder="Condition actuelle" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="excellent">Excellent - Peu de travaux</SelectItem>
                    <SelectItem value="good">Bon - Quelques améliorations</SelectItem>
                    <SelectItem value="fair">Correct - Rénovations importantes</SelectItem>
                    <SelectItem value="poor">À rénover complètement</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: "#283747" }}>
                  Exigences d'accès particulières
                </label>
                <Textarea
                  rows={3}
                  value={formData.accessRequirements}
                  onChange={(e) => handleInputChange("accessRequirements", e.target.value)}
                  className="border-2 border-slate-200 focus:border-yellow-500 resize-none"
                  placeholder="Stationnement, ascenseur, restrictions d'horaires, accès difficile, etc."
                />
              </div>
            </CardContent>
          </Card>
        );
        
      case 4:
        return (
          <Card className="shadow-xl">
            <CardHeader>
              <CardTitle className="flex items-center text-2xl" style={{ color: "#283747" }}>
                <Calendar className="w-6 h-6 mr-3" />
                Préférences et Planning
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: "#283747" }}>
                  Méthode de contact préférée
                </label>
                <Select value={formData.preferredContact} onValueChange={(value) => handleInputChange("preferredContact", value)}>
                  <SelectTrigger className="border-2 border-slate-200 focus:border-yellow-500">
                    <SelectValue placeholder="Comment préférez-vous être contacté ?" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="phone">Téléphone</SelectItem>
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="text">Messages texte</SelectItem>
                    <SelectItem value="whatsapp">WhatsApp</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: "#283747" }}>
                  Date préférée pour la visite d'évaluation
                </label>
                <Input
                  type="date"
                  value={formData.visitDate}
                  onChange={(e) => handleInputChange("visitDate", e.target.value)}
                  className="border-2 border-slate-200 focus:border-yellow-500"
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: "#283747" }}>
                  Informations additionnelles
                </label>
                <Textarea
                  rows={4}
                  value={formData.additionalInfo}
                  onChange={(e) => handleInputChange("additionalInfo", e.target.value)}
                  className="border-2 border-slate-200 focus:border-yellow-500 resize-none"
                  placeholder="Allergies, animaux de compagnie, préférences particulières, questions spécifiques..."
                />
              </div>
            </CardContent>
          </Card>
        );
        
      case 5:
        return (
          <Card className="shadow-xl">
            <CardHeader>
              <CardTitle className="flex items-center text-2xl" style={{ color: "#283747" }}>
                <CheckCircle className="w-6 h-6 mr-3" />
                Résumé de Votre Demande
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold mb-3" style={{ color: "#283747" }}>Contact</h4>
                  <div className="text-sm space-y-1 text-slate-600">
                    <p><strong>Nom:</strong> {formData.name}</p>
                    <p><strong>Email:</strong> {formData.email}</p>
                    <p><strong>Téléphone:</strong> {formData.phone}</p>
                    <p><strong>Adresse:</strong> {formData.address}</p>
                  </div>
                </div>
                
                <div>
                  <h4 className="font-semibold mb-3" style={{ color: "#283747" }}>Projet</h4>
                  <div className="text-sm space-y-1 text-slate-600">
                    <p><strong>Services:</strong> {formData.services.length} sélectionnés</p>
                    <p><strong>Budget:</strong> {formData.budget}</p>
                    <p><strong>Délai:</strong> {formData.timeline}</p>
                    <p><strong>Type:</strong> {formData.propertyType}</p>
                  </div>
                </div>
              </div>
              
              <div 
                className="p-6 rounded-xl text-white"
                style={{ backgroundColor: "#283747" }}
              >
                <div className="flex items-center mb-3">
                  <Clock className="w-5 h-5 mr-2" />
                  <h4 className="font-semibold">Prochaines Étapes</h4>
                </div>
                <ul className="text-sm space-y-2 opacity-90">
                  <li>✓ Nous recevrons votre demande immédiatement</li>
                  <li>✓ Un spécialiste vous contactera sous 24h</li>
                  <li>✓ Planification de la visite d'évaluation gratuite</li>
                  <li>✓ Présentation du devis détaillé sous 48h</li>
                </ul>
              </div>
              
              <div 
                className="flex items-start space-x-3 p-4 rounded-lg border-2"
                style={{ borderColor: "#D4A86A", backgroundColor: "#FFF9E6" }}
              >
                <AlertCircle className="w-5 h-5 mt-0.5" style={{ color: "#A04030" }} />
                <div className="text-sm">
                  <p className="font-medium mb-1" style={{ color: "#283747" }}>
                    Garantie de Satisfaction
                  </p>
                  <p className="text-slate-600">
                    Tous nos travaux sont garantis {companyInfo.warranty} et nous nous engageons 
                    à votre satisfaction complète.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        );
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl lg:text-5xl font-bold mb-6" style={{ color: "#283747" }}>
            {t('quotePage.title')}
          </h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            {t('quotePage.description')}
          </p>
        </div>

        {/* Step Indicator */}
        <StepIndicator />

        {/* Form Content */}
        <div className="mb-8">
          {renderStep()}
        </div>

        {/* Navigation Buttons */}
        <div className="flex justify-between items-center">
          <Button
            variant="outline"
            onClick={prevStep}
            disabled={currentStep === 1}
            className="border-2 border-slate-300 text-slate-700 hover:border-yellow-500 hover:text-slate-800 px-6 py-3"
          >
            ← Précédent
          </Button>
          
          <div className="text-sm text-slate-500">
            Étape {currentStep} sur 5
          </div>
          
          {currentStep < 5 ? (
            <Button
              onClick={nextStep}
              className="text-white font-medium px-6 py-3 rounded-lg transition-all duration-300 hover:scale-105"
              style={{ backgroundColor: "#A04030" }}
            >
              Suivant →
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              className="text-white font-medium px-8 py-3 rounded-lg transition-all duration-300 hover:scale-105"
              style={{ backgroundColor: "#A04030" }}
            >
              Envoyer ma Demande
            </Button>
          )}
        </div>

        {/* Trust Indicators */}
        <div className="mt-12 text-center">
          <div className="flex flex-col md:flex-row justify-center items-center space-y-4 md:space-y-0 md:space-x-8 text-sm text-slate-600">
            <div className="flex items-center">
              <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
              Devis 100% gratuit
            </div>
            <div className="flex items-center">
              <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
              Sans engagement
            </div>
            <div className="flex items-center">
              <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
              Réponse sous 24h
            </div>
            <div className="flex items-center">
              <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
              Visite d'évaluation gratuite
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Quote;